import Hammer from 'hammerjs';
import { ProductType } from '@/shared/enums';
import { breakpoint } from '@/shared/utils';
import { DeviceInfo } from '@/shared/libs';
import { ANIMATION_DELAY, SLIDE_SIZES } from '../config';

export default {
  inject: ['$section'],
  data() {
    return {
      sliderIndex: 0,
      slideSize: 1,
      hasNext: false,
      offset: 0,
      hasPrev: false,
      delay: ANIMATION_DELAY,
      liveManager: this.$services.resolve('liveManager'),
    };
  },
  computed: {
    elementsCount() {
      const elements = this.hideFirstElement ? this.products.length - 1 : this.products.length;
      return elements + this.extraElements;
    },
    endIndex() {
      const offset = this.offset < 0 ? 0 : this.offset;
      const lastIndex = (this.sliderIndex + offset + 2 * this.slideSize) * this.lines;

      return Math.min(lastIndex, this.elementsCount);
    },
    transformOffset() {
      const position = this.offset + this.sliderIndex;
      return (position / this.slideSize) * -100;
    },
    visibleProducts() {
      return this.products.slice(0, this.endIndex);
    },
    products() {
      const products = this.$section.products.length
        ? this.$section.products
        : this.$section.resource.elements.collection.records;

      return products.filter((product) => {
        if (product.type === ProductType.PROGRAMME) {
          const channel = this.liveManager.channels.get(product.channelId);
          return channel && product;
        }
        return product;
      });
    },
    spareSpace() {
      return this.additionalItem || 0;
    },
    visibleSlideSize() {
      return this.slideSize - this.spareSpace;
    },
    wrapperStyle() {
      return {
        transform: `translate3d(${this.transformOffset}%, 0, 0)`,
        transitionDuration: `${this.delay}ms`,
      };
    },
  },
  methods: {
    attachSlider() {
      this.updateSlideSize();
      this.updateSliderArrows();
      this.setSectionWidth();
      this.slideToInitialIndex();
      this.hammer = new Hammer(this.$refs.slider, {
        touchAction: 'pan-y',
        recognizers: [
          [Hammer.Swipe, { direction: Hammer.DIRECTION_HORIZONTAL }],
          [Hammer.Pan, { direction: Hammer.DIRECTION_HORIZONTAL }, ['swipe']],
        ],
        enable: DeviceInfo.isTouchDevice(),
      });
      this.hammer.on('panstart', this.onPanStart);
      this.hammer.on('panleft panright', this.onPanHorizontal);
      this.hammer.on('panend pancancel', this.onPanStop);
      this.hammer.on('swipeleft', this.onSwipeLeft);
      this.hammer.on('swiperight', this.onSwipeRight);
    },
    detachSlider() {
      if (!this.hammer) {
        return;
      }

      this.hammer.off('panstart', this.onPanStart);
      this.hammer.off('panleft panright', this.onPanHorizontal);
      this.hammer.off('panend pancancel', this.onPanStop);
      this.hammer.off('swipeleft', this.onSwipeLeft);
      this.hammer.off('swiperight', this.onSwipeRight);
      this.hammer.destroy();
    },
    onPanStart(event) {
      event.preventDefault();
      const { center } = event;
      const wrapper = this.$refs['slider-wrapper'];
      const wrapperRect = wrapper.getBoundingClientRect();
      const parentRect = this.$section.$el.querySelector('.js-section-container').getBoundingClientRect();

      this.panXStart = center.x;
      this.wrapperXStart = wrapperRect.x - parentRect.x;
    },
    onPanHorizontal(event) {
      event.preventDefault();
      const { center } = event;
      const wrapper = this.$refs['slider-wrapper'];
      const position = this.panXStart - center.x;

      if (this.offset !== 0 || !this.panXStart) {
        return;
      }

      wrapper.style.transitionDuration = '0s';
      wrapper.style.transform = `translate3d(calc(${this.wrapperXStart}px + ${-position}px), 0, 0)`;
    },
    onPanStop(event) {
      event.preventDefault();
      const { center, direction } = event;
      const wrapper = this.$refs['slider-wrapper'];
      const slidingLeft = direction === Hammer.DIRECTION_LEFT || (center.x && this.panXStart > center.x);
      const slidingRight = direction === Hammer.DIRECTION_RIGHT || (center.x && this.panXStart < center.x);

      wrapper.style.transitionDuration = `${this.delay}ms`;

      if (!this.panXStart) {
        this.abort();
        return;
      }

      if (slidingLeft && this.hasNext) {
        this.next();
      } else if (slidingRight && this.hasPrev) {
        this.prev();
      } else {
        this.abort();
      }
    },
    onSwipeLeft() {
      this.panXStart = 0;
      if (this.hasNext) {
        this.next();
      }
    },
    onSwipeRight() {
      this.panXStart = 0;
      if (this.hasPrev) {
        this.prev();
      }
    },
    updateSlideSize() {
      this.slideSize = SLIDE_SIZES[this.layout][breakpoint.current()];
      this.$el.style.setProperty('--tiles-number', this.slideSize);
    },
    updateSliderArrows() {
      this.hasNext = this.elementsCount > (this.sliderIndex + this.visibleSlideSize) * this.lines;
      this.hasPrev = this.sliderIndex > 0;
    },
    slideToInitialIndex() {
      this.$nextTick(() => {
        this.slideTo(this.$section.initialFocusIndex, 0, { center: !!this.$section.initialFocusIndex });
        this.updateSliderState();
      });
    },
    slideTo(index = 0, duration, opts = {}) {
      const oldIndex = this.sliderIndex;
      const maxIndex = Math.max(Math.round(this.elementsCount / this.lines) - this.visibleSlideSize, 0);
      let newIndex = index;

      if (opts.center) {
        newIndex -= Math.round(this.visibleSlideSize / 2) - 1;
      }

      newIndex = Math.min(newIndex, maxIndex);
      newIndex = Math.max(newIndex, 0);

      if (this.offset + newIndex > maxIndex || this.offset + newIndex < 0) {
        return;
      }

      this.offset += (newIndex - oldIndex);
      this.delay = duration;
    },
    prev() {
      this.slideTo(this.sliderIndex - this.visibleSlideSize, ANIMATION_DELAY);
    },
    next() {
      this.slideTo(this.sliderIndex + this.visibleSlideSize, ANIMATION_DELAY);
    },
    abort() {
      const wrapper = this.$refs['slider-wrapper'];

      wrapper.style.transitionDuration = `${ANIMATION_DELAY}ms`;
      wrapper.style.transform = `translate3d(${this.transformOffset}%, 0, 0)`;
    },
    updateSliderState() {
      this.sliderIndex += this.offset;
      this.offset = 0;
      this.delay = 0;
      this.updateSliderArrows();
    },
  },
};
